/**
 * <Seo> — per-page head tags via react-helmet-async.
 *
 * Renders title, description, canonical, Open Graph + Twitter Card, robots,
 * and optional per-page JSON-LD. react-helmet-async is SSG-friendly, so these
 * same tags will land in static HTML once the Phase-6 prerender pass is added.
 */
import { Helmet } from 'react-helmet-async'
import { tenantConfig } from '../config/tenant'
import { DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, absoluteUrl } from '../config/seo'

/** Normalise a route path to its canonical trailing-slash form (root stays "/"). */
function withTrailingSlash(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  if (p === '/' || p.endsWith('/')) return p
  return `${p}/`
}

interface SeoProps {
  /** Page title without the brand suffix — we append " — <Tenant>" unless already present. */
  title: string
  description?: string
  /** Route path for the canonical URL, e.g. "/about". */
  path: string
  /** Social-share image (path from root or absolute URL). */
  image?: string
  type?: 'website' | 'article'
  /** Set for auth/utility pages that shouldn't be indexed. */
  noindex?: boolean
  /** Extra structured data for this page (single object or array). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const fullTitle = title.includes(tenantConfig.name) ? title : `${title} · ${tenantConfig.name}`
  // Canonical + og:url use the TRAILING-SLASH form, because Netlify serves the
  // prerendered dist/<route>/index.html at /route/ (and 301s /route -> /route/).
  // Matching here keeps sitemap == served == canonical (no "Page with redirect").
  const canonical = absoluteUrl(withTrailingSlash(path))
  const ogImage = absoluteUrl(image)
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={tenantConfig.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  )
}
